import { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { GET_QUESTIONS } from '@queries/questions';
import Questionnaire from '@components/questionnaire';
import Breadcrumb from '@components/breadcrumb';
import NextNav, { SaveFunction } from '@components/next-nav';
import { TripDigestContext } from '@contexts/trip-digest-context';
import { QuestionState } from '@generated/graphql';

import ErrorPage from '@components/error-page';

function SurveyLayers() {
  // get cached questions
  const { questionStates } = useContext(TripDigestContext)!;
  const { clientId, tripProfileId } = useParams();
  // get cached questions
  const apolloClient = useApolloClient();
  const questions = apolloClient.readQuery({
    query: GET_QUESTIONS,
  });

  // handle when data is undefined due to apollo error
  if (!questions) {
    Sentry.captureException(Error('Unable to fetch questions from cache for SurveyLayers'), {
      extra: { questions: JSON.stringify(questions, null, 2) },
    });
    return <ErrorPage message="Unable to fetch questions from cache for SurveyLayers" />;
  }

  const selectedIds: string[] = [];
  // console.log('>>>> questionStates: ', questionStates);
  questionStates.forEach((state: QuestionState) => {
    if (state.questionType === 'LAYER_GROUP') {
      // get the matching question object for this state
      // console.log('>>>> questions:', questions);
      const matchedQuestion = questions.findQuestions.filter((question) => question.id === state.questionId)[0];
      // console.log('##### matchedQuestion:', state, matchedQuestion);
      // get the first matched choice object for this question & state (rank)
      const matchedChoice = matchedQuestion.choices.filter((choice) => choice.id === state.rank?.[0])[0];
      if (!matchedChoice) {
        const msg = Error(`Unable to find matched choice for SurveyLayers - state.questionId: [${state.questionId}]`);

        console.error(msg, state);
        Sentry.captureException(msg, {
          extra: { matchedChoice: JSON.stringify(matchedChoice, null, 2) },
        });
        return <ErrorPage message="Unable to find matched choice for SurveyLayers" />;
      }
      selectedIds.push(matchedChoice.layerQuestionId ? matchedChoice.layerQuestionId.toString() : '');
    }
    return null;
  });
  const layerQuestions = questions.findQuestions.filter((question) => selectedIds.includes(question.id));

  return (
    <div>
      <Breadcrumb pageStep={1} />
      <Questionnaire title="Travel Themes - Layers" questions={layerQuestions} />
      <NextNav
        next={`/clients/${clientId}/trip-profiles/${tripProfileId}/map-results`}
        saveFunctionName={SaveFunction.SaveQuestionnaire}
      />
    </div>
  );
}

export default SurveyLayers;
